import React, { memo, useState, useEffect } from 'react';
import { Carousel, Flex } from 'antd-mobile';
import { GoodItem } from '../components';
import _ from 'lodash';
import PropTypes from 'prop-types';

const CAROUSEL_HEIGHT = {
  SWIPER: {
    CONSUMER_ACTIVITY: '1.7rem',
    WITH_SELL_POINT: '1.4rem',
    DEFAULT: '1.08rem'
  },
  SCROLL: {
    CONSUMER_ACTIVITY: '1.74rem',
    WITH_SELL_POINT: '1.52rem',
    DEFAULT: '1.2rem'
  }
};

const defaultProps = {
  data: {
    itemList: []
  },
  config: {},
  showPoints: true,
  horizontal: false,
  horizontalConfig: {
    showBullets: true
  },
  vertical: false,
  productParams: {},
  onPress: null
};

// 商品数据结构
const itemInfoPropTypes = PropTypes.shape({
  id: PropTypes.string, // 商品ID
  itemId: PropTypes.string, // 商品ID（兼容字段）
  imgUrl: PropTypes.string, // 商品图片
  name: PropTypes.string, // 商品名称
  price: PropTypes.number, // 商品价格
  discountSalePrice: PropTypes.number, // 消费活动会员价
  itemStyle: PropTypes.number, // 产品样式，0-默认，1-会员立省
  hasActivity: PropTypes.number, // 是否有活动，0-无，1-有
  maxPrice: PropTypes.number, // 商品最高价格
  type: PropTypes.number // 商品类型（6：多规格商品）
});

// 卖点数据结构
const sellPointPropTypes = PropTypes.shape({
  sellPointColour: PropTypes.string, // 卖点颜色（支持'FF0000'或'#FF0000'格式）
  sellPointName: PropTypes.string // 卖点文案
});

// 商品列表项数据结构
const itemListPropTypes = PropTypes.arrayOf(
  PropTypes.shape({
    itemInfo: itemInfoPropTypes, // 商品信息
    activityId: PropTypes.string, // 活动ID
    activityType: PropTypes.number, // 活动类型
    discountSalePrice: PropTypes.number, // 消费活动会员价
    itemStyle: PropTypes.number, // 产品样式，0-默认，1-会员立省
    sellPointList: PropTypes.arrayOf(sellPointPropTypes) // 卖点列表
  })
);

// 横向布局配置
const horizontalConfigPropTypes = PropTypes.shape({
  size: PropTypes.number.isRequired, // 单个商品宽度
  count: PropTypes.number.isRequired, // 每行显示个数
  showBullets: PropTypes.bool // 是否显示轮播指示器
});

// 纵向布局配置
const verticalConfigPropTypes = PropTypes.shape({
  size: PropTypes.number.isRequired, // 单个商品宽度
  count: PropTypes.number.isRequired // 每行显示个数
});

const propTypes = {
  width: PropTypes.number.isRequired, // 容器宽度
  containerStyle: PropTypes.object, // 容器样式
  data: PropTypes.shape({
    itemList: itemListPropTypes.isRequired, // 商品列表
    hasActivity: PropTypes.number // 是否有活动，0-无，1-有
  }),
  showPoints: PropTypes.bool, // 是否显示卖点
  config: PropTypes.shape({
    reverse: PropTypes.bool, // 开启这个属性后，产品名称和卖点的顺序会互换
    radius: PropTypes.string // 圆角值
  }),
  horizontal: PropTypes.bool, // 是否横向布局
  vertical: PropTypes.bool, // 是否纵向布局
  horizontalConfig: horizontalConfigPropTypes, // 横向布局配置
  verticalConfig: verticalConfigPropTypes, // 纵向布局配置
  productParams: PropTypes.object, // Object 商品参数
  onPress: PropTypes.func, // 点击事件回调
  hasActivity: PropTypes.number // 是否有活动，0-无，1-有
};

const GoodsCell = memo((props) => {
  const {
    containerStyle, // Object 容器样式
    width, // Number 宽度
    data, // Object 数据源
    horizontal, // Boolen false 纵向展示 true 横向展示
    vertical, // Boolen false 纵向展示 true 横向展示
    horizontalConfig, // Object 横向配置
    verticalConfig, // Object 纵向配置
    showPoints, // Boolean 是否显示卖点
    config, // Object 配置项
    productParams, // Object 商品参数
    onPress, // Function 点击事件
    autoplay: autoplayProp, // Boolean 自动轮播
    infinite: infiniteProp, // Boolean 无限轮播
    hasActivity
  } = props;

  const [nowPage, setNowPage] = useState(1); // 当前页数
  const [showPoint, setShowPoint] = useState(false); // 是否显示卖点

  const {
    itemList, // Array 数据列表
    hasActivity: dataHasActivity // Number 是否有活动，0-无，1-有
  } = data;
  const activityFlag = hasActivity || dataHasActivity;

  // hasActivity 控制楼层是否有活动，itemStyle 控制单个商品是否使用会员立省样式。
  // GoodItem 也兼容 itemInfo.hasActivity，所以这里保持同一判断，避免卡片展示新样式但外层高度仍按老样式裁切。
  const hasConsumerActivityItem = (itemList || []).some((item) => {
    const { itemInfo = {} } = item || {};
    return (+activityFlag === 1 || +itemInfo.hasActivity === 1) && +itemInfo.itemStyle === 1;
  });

  const {
    reverse = false, // Boolean 开启这个属性后，产品名称和卖点的顺序会互换
    radius = 0 // String 圆角值
  } = config || {};

  useEffect(() => {
    // 任意有一个商品存在卖点，那整栏商品都要有卖点
    let result = (itemList || []).some((item) => (item.sellPointList || []).length > 0);
    showPoints && setShowPoint(result);
  }, [data, showPoints]);

  const renderBullets = () => {
    const {
      count, // Number 每行内容数量
      showBullets = true, // Boolean 是否显示指示器
      bulletsConfig = {
        marginTop: '0.04rem'
      }
    } = horizontalConfig;
    if (!showBullets) return null;
    const list = _.chunk(itemList, count);

    // Carousel的页数
    const length = list.length || 1;

    // 指示器总宽度
    const viewW = '0.56rem';
    const nowW = `calc(${viewW} * (${nowPage / length}))`;

    return (
      <Flex
        direction="row"
        align="center"
        style={{
          alignSelf: 'center',
          width: viewW,
          height: '0.04rem',
          backgroundColor: '#F5F7FAaa',
          marginTop: bulletsConfig.marginTop
        }}>
        <div
          style={{
            width: nowW,
            height: '0.04rem',
            backgroundColor: '#fff'
          }}
        />
      </Flex>
    );
  };

  // 纵向布局
  const renderVertical = () => {
    if (horizontal) return null;
    const {
      size, // Number 每行内容尺寸
      count // Number 每行内容数量
    } = verticalConfig;
    let marginL = `calc((${width} - ${count} * ${size}) / ${count - 1})`;
    if (marginL < 0) {
      marginL = 0;
    }

    return (
      <Flex direction="row" wrap="wrap">
        {itemList.map((item, index) => {
          const { itemInfo, sellPointList } = item;
          return (
            <Flex
              key={index}
              style={{
                marginLeft: index % count != 0 ? marginL : 0,
                marginTop: index < count ? 0 : marginL
              }}>
              <GoodItem
                width={size}
                data={itemInfo}
                config={{
                  reverse,
                  radius
                }}
                productParams={productParams}
                showSellPoint={showPoint}
                sellPointList={(sellPointList || []).filter((v, i) => i < (count > 2 ? 1 : 2))}
                onPress={() => onPress && onPress(item)}
                hasActivity={activityFlag}
              />
            </Flex>
          );
        })}
      </Flex>
    );
  };

  // 横向布局
  const renderHorizontal = () => {
    if (vertical) return null;

    const {
      type = 'swiper' // String 排布类型
    } = horizontalConfig;

    return (
      <React.Fragment>
        {/* 横向布局 */}
        {type == 'swiper' && renderSwiper()}
        {type == 'scroll' && renderScroll()}
      </React.Fragment>
    );
  };

  const renderSwiper = () => {
    const {
      size, // Number 每行内容尺寸
      count // Number 每行内容数量
    } = horizontalConfig;
    const list = _.chunk(itemList, count);
    let marginL = `calc((${width} - ${count} * ${size}) / ${count - 1})`;
    if (marginL < 0) {
      marginL = 0;
    }

    return (
      <Flex direction="column">
        <Carousel
          style={{
            width,
            height: hasConsumerActivityItem
              ? `calc(${size} + ${CAROUSEL_HEIGHT.SWIPER.CONSUMER_ACTIVITY})`
              : showPoint
                ? `calc(${size} + ${CAROUSEL_HEIGHT.SWIPER.WITH_SELL_POINT})`
                : `calc(${size} + ${CAROUSEL_HEIGHT.SWIPER.DEFAULT})`
          }}
          autoplay={autoplayProp || false}
          infinite={infiniteProp || false}
          dots={false}
          afterChange={(index) => setNowPage(index + 1)}>
          {list.map((item, index) => {
            return (
              <Flex
                key={index}
                direction="row"
                style={{
                  width
                }}>
                {item.map((_item, _index) => {
                  const { itemInfo, sellPointList } = _item || {};
                  return (
                    <Flex
                      key={_index}
                      style={{
                        marginLeft: _index !== 0 ? marginL : 0
                      }}>
                      <GoodItem
                        width={size}
                        data={itemInfo}
                        config={{
                          reverse,
                          radius
                        }}
                        productParams={productParams}
                        showSellPoint={showPoint}
                        sellPointList={(sellPointList || []).filter(
                          (v, i) => i < (count > 2 ? 1 : 2)
                        )}
                        onPress={() => onPress && onPress(_item)}
                        hasActivity={activityFlag}
                      />
                    </Flex>
                  );
                })}
              </Flex>
            );
          })}
        </Carousel>
        {renderBullets()}
      </Flex>
    );
  };

  const renderScroll = () => {
    let marginL = '0.12rem';
    const {
      size // Object 单个推荐位尺寸
    } = horizontalConfig;

    return (
      <Flex
        style={{
          width,
          height: hasConsumerActivityItem
            ? `calc(${size} + ${CAROUSEL_HEIGHT.SCROLL.CONSUMER_ACTIVITY})`
            : showPoint
              ? `calc(${size} + ${CAROUSEL_HEIGHT.SCROLL.WITH_SELL_POINT})`
              : `calc(${size} + ${CAROUSEL_HEIGHT.SCROLL.DEFAULT})`
        }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            overflowX: 'scroll',
            overflowY: 'hidden'
          }}>
          {itemList.map((item, index) => {
            return (
              <Flex
                key={index}
                style={{
                  flexShrink: 0,
                  marginLeft: index != 0 ? marginL : 0
                }}>
                <GoodItem
                  width={size}
                  data={item.itemInfo}
                  config={{
                    reverse,
                    radius
                  }}
                  productParams={productParams}
                  showSellPoint={showPoint}
                  sellPointList={item.sellPointList}
                  onPress={() => onPress && onPress(item)}
                  hasActivity={activityFlag}
                />
              </Flex>
            );
          })}
        </div>
      </Flex>
    );
  };

  return (
    <Flex style={containerStyle}>
      {/* 横向布局 */}
      {renderHorizontal()}
      {/* 纵向布局 */}
      {renderVertical()}
    </Flex>
  );
});

GoodsCell.defaultProps = defaultProps;
GoodsCell.propTypes = propTypes;

export default GoodsCell;



// WEBPACK FOOTER //
// ./src/pubBiz/CommonCell/GoodsCell/index.js